import { RAIL_FILTERS } from './schoolEvents';

export const RECRUIT_MODES = ['me', 'schools'];
// Retained only so the F2 redirects (/offers -> ?tab=offers) still parse. The
// Schools tabs themselves are gone — F3 replaced them with the rail, and a
// legacy tab now resolves to the equivalent filter via parseRailFilter.
export const RECRUIT_TABS = ['matches', 'following', 'visits', 'offers'];

// The fallback when there is no profile to judge by yet — see
// defaultRecruitMode for the actual rule, which activated with F1 now that
// Recruit > Me has real content.
export const DEFAULT_MODE = 'schools';
export const DEFAULT_TAB = 'matches';

/**
 * Which mode a Recruit visit opens on when the URL doesn't say.
 *
 * An emptiness test, not a percentage. The apps use "below 80% profile
 * completion", but web and iOS count completion differently — iOS walks ten
 * profile sections, web's dashboard counts three field groups — so a shared
 * threshold would land the same athlete in different modes on laptop and
 * phone, which reads as a bug rather than parity. Empty/not-empty cannot
 * drift. See the F2 design doc §5.
 */
export function defaultRecruitMode({ hasMeasurables, hasFilm }) {
  return (!hasMeasurables && !hasFilm) ? 'me' : 'schools';
}

/**
 * Map ?mode= / ?tab= onto the Recruit surface's state.
 *
 * A stale bookmark or a hand-edited link should land somewhere sensible rather
 * than rendering an empty page, so anything unrecognised falls back. The two
 * values are validated independently — a good tab still works alongside a
 * garbled mode.
 *
 * @param {{get: (key: string) => (string|null)}} params URLSearchParams or equivalent.
 * @returns {{mode: string, tab: string}}
 */
export function parseRecruitParams(params) {
  const rawMode = params.get('mode');
  const rawTab = params.get('tab');
  return {
    mode: RECRUIT_MODES.includes(rawMode) ? rawMode : DEFAULT_MODE,
    tab: RECRUIT_TABS.includes(rawTab) ? rawTab : DEFAULT_TAB,
  };
}

/**
 * Build the /recruit URL an old recruiting route should land on.
 *
 * Every param on the incoming URL is carried through — /matches?peek=<unitid>
 * is a live review affordance that MatchesPanel still reads, and a static
 * redirect would drop it. Incoming params also win over the defaults, so an
 * explicit ?tab= is respected rather than overwritten.
 *
 * @param {string} tab the tab this old route maps to
 * @param {URLSearchParams} incoming params on the URL being redirected
 * @returns {string} e.g. "/recruit?mode=schools&tab=matches&peek=214777"
 */
export function buildRecruitTarget(tab, incoming) {
  const next = new URLSearchParams();
  next.set('mode', 'schools');
  next.set('tab', tab);
  if (incoming) incoming.forEach((value, key) => next.set(key, value));
  return `/recruit?${next.toString()}`;
}

// F2 redirected /offers, /visits and /following onto ?tab=. The Schools tabs
// are gone in F3, so a legacy tab resolves to the equivalent rail filter and
// those bookmarks keep landing somewhere honest instead of being ignored.
const TAB_TO_FILTER = {
  offers: 'offers',
  visits: 'visits',
  following: 'following',
  matches: 'all',
};

/**
 * Which rail filter is active. An explicit ?filter= wins; otherwise a legacy
 * ?tab= maps onto its filter; otherwise everything.
 */
export function parseRailFilter(params) {
  const explicit = params.get('filter');
  if (RAIL_FILTERS.includes(explicit)) return explicit;
  return TAB_TO_FILTER[params.get('tab')] || 'all';
}
